/**
 * ConnectionManager - Manages connections between tasks in the timeline
 * Handles creation, deletion, and visualization of task dependencies
 */
class ConnectionManager {
  /**
   * Create a ConnectionManager
   * @param {TimelineCalendar} timeline - The timeline instance
   * @param {DependencyManager} dependencyManager - The dependency manager instance
   */
  constructor(timeline, dependencyManager) {
    this.timeline = timeline;
    this.isConnectionMode = false;
    this.connectingFrom = null;
    this.dependencyManager = dependencyManager;

    this.showAllConnections = timeline.options.showAllConnections !== undefined ? timeline.options.showAllConnections : false;
    this.selectedBlock = null;
    this.visibleConnections = new Set();
  }

  /**
   * Toggle connection visibility for all connections
   * @returns {boolean} New visibility state
   */
  toggleConnectionVisibility() {
    this.showAllConnections = !this.showAllConnections;
    this.updateVisibleConnections();
    this.updateConnections();
    return this.showAllConnections;
  }

  /**
   * Select a block and show its connections
   * @param {Object} block - Block data object
   * @param {HTMLElement} element - Block DOM element
   */
  selectBlock(block, element) {
    if (this.selectedBlock) {
      const prevElement = document.querySelector(`.task-block[data-id="${this.selectedBlock.id}"].selected`);
      if (prevElement) {
        prevElement.classList.remove('selected');
      }
    }

    if (this.selectedBlock && this.selectedBlock.id === block.id) {
      this.selectedBlock = null;
      element.classList.remove('selected');
    } else {
      this.selectedBlock = block;
      element.classList.add('selected');
    }

    this.updateVisibleConnections();
    this.updateConnections();
  }

  /**
   * Update the set of visible connections based on selection state
   */
  updateVisibleConnections() {
    this.visibleConnections.clear();

    if (!this.showAllConnections && this.selectedBlock) {
      const fromConnections = this.getConnectionsFromBlock(this.selectedBlock.id);
      const toConnections = this.getConnectionsToBlock(this.selectedBlock.id);

      fromConnections.forEach(conn => this.visibleConnections.add(conn.id));
      toConnections.forEach(conn => this.visibleConnections.add(conn.id));
    }
  }

  /**
   * Handle click on a block when in connection mode
   * @param {Object} block - Block data object
   * @param {HTMLElement} element - Block DOM element
   */
  async handleConnectionClick(block, element) {
    if (!this.connectingFrom) {
      this.connectingFrom = block;
      element.classList.add('connecting');
      this.timeline.showNotification(this.timeline.t('Select destination block'), 'info', 2000);
    } else {
      if (this.connectingFrom.id === block.id) {
        element.classList.remove('connecting');
        this.connectingFrom = null;
        this.timeline.showNotification(this.timeline.t('Connection cancelled'), 'info', 1500);
        return;
      }

      const fromId = this.connectingFrom.id;
      const toId = block.id;

      const existingConnection = this.dependencyManager.connections.find(
        c => c.fromId === fromId && c.toId === toId
      );

      if (existingConnection) {
        this.timeline.showNotification(this.timeline.t('Connection already exists'), 'warning');
        document.querySelector(`.task-block[data-id="${this.connectingFrom.id}"]`)?.classList.remove('connecting');
        this.connectingFrom = null;
        return;
      }

      if (this.dependencyManager.willCreateCycle(fromId, toId)) {
        this.timeline.showNotification(this.timeline.t('Connection failed: Would create cycle'), "error");
        document.querySelector(`.task-block[data-id="${this.connectingFrom.id}"]`)?.classList.remove('connecting');
        this.connectingFrom = null;
        return;
      }

      try {
        const tempId = this.dependencyManager.addConnection(fromId, toId);

        if (tempId) {
          const connection = this.getConnection(tempId);

          if (connection) {
            this.updateConnections();

            try {
              const result = await this.sendConnectionToAPI(connection);

              if (result && result.success) {
                // sendRequest hands back {success, data: <ApiService wrapper>},
                // so the response body is one level further in.
                if (result.data && result.data.data && result.data.data.connectionId) {
                  connection.id = result.data.data.connectionId;

                  this.updateConnections();
                }

                this.timeline.showNotification(this.timeline.t('Connection added successfully'), 'success');
              } else {
                console.error('Failed to add connection on server:', result?.error);
                this.dependencyManager.removeConnection(tempId);
                this.updateConnections();

                this.timeline.showNotification(
                  this.timeline.t('Failed to add connection on server: {message}', {message: result?.error || 'Unknown error'}),
                  'error'
                );
              }
            } catch (error) {
              console.error('Exception when saving connection:', error);

              this.dependencyManager.removeConnection(tempId);
              this.updateConnections();

              this.timeline.showNotification(
                this.timeline.t('Error saving connection: {message}', {message: error.message}),
                'error'
              );
            }

            this.timeline.triggerCallback('onConnectionCreate', {
              id: connection.id || tempId,
              fromId,
              toId,
              gap: connection.gap
            });
            this.timeline.triggerDataChangeCallback();
          }
        }
      } catch (error) {
        console.error('Error creating connection:', error);
        this.timeline.showNotification(this.timeline.t('Error creating connection'), 'error');
      }

      document.querySelector(`.task-block[data-id="${this.connectingFrom.id}"]`)?.classList.remove('connecting');
      this.connectingFrom = null;
    }
  }

  /**
   * Update SVG connections based on current blocks
   */
  updateConnections() {
    if (!this.timeline.connectionSvg || !this.timeline.timelineContainer) return;

    const containerWidth = this.timeline.timelineContainer.scrollWidth;
    const containerHeight = this.timeline.timelineContainer.scrollHeight;

    this.timeline.connectionSvg.setAttribute("width", containerWidth);
    this.timeline.connectionSvg.setAttribute("height", containerHeight);

    while (this.timeline.connectionSvg.firstChild) {
      this.timeline.connectionSvg.removeChild(this.timeline.connectionSvg.firstChild);
    }

    if (!this.dependencyManager.connections || this.dependencyManager.connections.length === 0) return;

    const scrollLeft = this.timeline.timelineContainer.scrollLeft;
    const scrollTop = this.timeline.timelineContainer.scrollTop;

    this.dependencyManager.connections.forEach((connection, index) => {
      // Skip if connections aren't visible based on settings
      if (!this.showAllConnections && !this.visibleConnections.has(connection.id || connection.tempId)) {
        return;
      }

      const fromBlock = this.timeline.dataManager.blocks.find(b => b.id === connection.fromId);
      const toBlock = this.timeline.dataManager.blocks.find(b => b.id === connection.toId);

      if (!fromBlock || !toBlock) return;

      const fromElement = document.querySelector(`.task-block[data-id="${fromBlock.id}"]`);
      const toElement = document.querySelector(`.task-block[data-id="${toBlock.id}"]`);

      if (!fromElement || !toElement) return;

      try {
        const fromRect = fromElement.getBoundingClientRect();
        const toRect = toElement.getBoundingClientRect();
        const containerRect = this.timeline.timelineContainer.getBoundingClientRect();

        const fromRight = fromRect.right - containerRect.left + scrollLeft;
        const fromMiddleY = fromRect.top + fromRect.height / 2 - containerRect.top + scrollTop;
        const toLeft = toRect.left - containerRect.left + scrollLeft;
        const toMiddleY = toRect.top + toRect.height / 2 - containerRect.top + scrollTop;

        this.drawSvgArrow(
          fromRight,
          fromMiddleY,
          toLeft,
          toMiddleY,
          this.timeline.options.connectionLineColor || '#000000',
          index,
          connection
        );
      } catch (error) {
        console.error('Error drawing connection:', connection, error);
      }
    });
  }

  /**
   * Draw an SVG arrow between two points
   * @param {number} fromX - Start X coordinate
   * @param {number} fromY - Start Y coordinate
   * @param {number} toX - End X coordinate
   * @param {number} toY - End Y coordinate
   * @param {string} color - Arrow color
   * @param {number} index - Connection index
   * @param {Object} connection - Connection data
   */
  drawSvgArrow(fromX, fromY, toX, toY, color, index, connection) {
    const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
    group.setAttribute("data-connection-id", index);

    const startCircle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    startCircle.setAttribute("cx", fromX);
    startCircle.setAttribute("cy", fromY);
    startCircle.setAttribute("r", this.timeline.options.arrowSize);
    startCircle.setAttribute("fill", color);
    group.appendChild(startCircle);

    const controlPointX = (fromX + toX) / 2;

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", `M${fromX},${fromY} C${controlPointX},${fromY} ${controlPointX},${toY} ${toX},${toY}`);
    path.setAttribute("stroke", color);
    path.setAttribute("stroke-width", this.timeline.options.lineThickness);
    path.setAttribute("fill", "none");
    group.appendChild(path);

    const endCircle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    endCircle.setAttribute("cx", toX);
    endCircle.setAttribute("cy", toY);
    endCircle.setAttribute("r", this.timeline.options.arrowSize);
    endCircle.setAttribute("fill", color);
    group.appendChild(endCircle);

    this.timeline.connectionSvg.appendChild(group);
  }

  /**
   * Setup arrow markers for SVG connections
   */
  setupArrowMarkers() {
    if (!this.timeline.connectionSvg.querySelector('defs')) {
      const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");

      const marker = document.createElementNS("http://www.w3.org/2000/svg", "marker");
      marker.setAttribute("id", "arrowhead");
      marker.setAttribute("markerWidth", "10");
      marker.setAttribute("markerHeight", "7");
      marker.setAttribute("refX", "9");
      marker.setAttribute("refY", "3.5");
      marker.setAttribute("orient", "auto");

      const polygon = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
      polygon.setAttribute("points", "0 0, 10 3.5, 0 7");
      polygon.setAttribute("fill", this.timeline.options.connectionLineColor || "#000000");

      marker.appendChild(polygon);
      defs.appendChild(marker);
      this.timeline.connectionSvg.appendChild(defs);
    }
  }

  /**
   * Calculate gap between two blocks in days
   * @param {Object} fromBlock - Source block
   * @param {Object} toBlock - Target block
   * @returns {number} - Gap in days
   */
  calculateGapBetweenBlocks(fromBlock, toBlock) {
    if (!fromBlock || !toBlock) return 1;

    const fromEndDate = this.timeline.dataManager.calculateEndDate(
      fromBlock.startDate,
      fromBlock.duration
    );

    if (!fromEndDate || !toBlock.startDate) return 1;

    const toStartDateObj = new Date(toBlock.startDate);
    const fromEndDateObj = new Date(fromEndDate);
    if (toStartDateObj <= fromEndDateObj) {
      return 0;
    }

    if (this.timeline.workingDaysManager && this.timeline.workingDaysManager.initialized) {
      const totalDays = this.timeline.workingDaysManager.calculateDuration(fromEndDate, toBlock.startDate);
      return Math.max(totalDays - 1, 0);
    }

    if (this.timeline.options.showHolidays) {
      const totalDays = this.timeline.dataManager.calculateWorkingDaysBetween(fromEndDate, toBlock.startDate);
      return Math.max(totalDays - 1, 0);
    } else {
      const gap = Math.floor((toStartDateObj - fromEndDateObj) / 86400000);
      return Math.max(gap, 0);
    }
  }

  /**
   * Update the gap for a specific connection
   * @param {string|number} connectionId - Connection ID
   */
  updateConnectionGap(connectionId) {
    const connection = this.getConnection(connectionId);
    if (!connection) return;

    const fromBlock = this.timeline.dataManager.blocks.find(b => b.id === connection.fromId);
    const toBlock = this.timeline.dataManager.blocks.find(b => b.id === connection.toId);

    connection.gap = this.calculateGapBetweenBlocks(fromBlock, toBlock);
  }

  /**
   * Update gaps for all connections
   */
  updateAllConnectionGaps() {
    if (!this.connections || this.connections.length === 0) return;

    this.connections.forEach(connection => {
      const fromBlock = this.timeline.dataManager.blocks.find(b => b.id === connection.fromId);
      const toBlock = this.timeline.dataManager.blocks.find(b => b.id === connection.toId);

      if (fromBlock && toBlock) {
        connection.gap = this.calculateGapBetweenBlocks(fromBlock, toBlock);
      }
    });
  }

  /**
   * Manually update a connection gap
   * @param {string|number} connectionId - Connection ID
   * @param {number} newGap - New gap value
   */
  async updateGapManually(connectionId, newGap) {
    const connection = this.getConnection(connectionId);
    if (!connection) return;

    const numGap = parseInt(newGap);
    if (isNaN(numGap) || numGap < 0) return;

    const originalGap = connection.gap;

    connection.gap = numGap;

    if (originalGap !== numGap) {
      const result = await this.sendConnectionToAPI(connection);

      if (result && result.success) {
        this.timeline.showNotification(this.timeline.t('Connection gap updated successfully'), 'success');

        if (this.timeline.options.syncConnectedBlocks) {
          const fromBlock = this.timeline.dataManager.blocks.find(b => b.id === connection.fromId);
          const toBlock = this.timeline.dataManager.blocks.find(b => b.id === connection.toId);

          if (fromBlock && toBlock) {
            const sourceEndDate = this.timeline.dataManager.calculateEndDate(
              fromBlock.startDate,
              fromBlock.duration
            );

            if (sourceEndDate) {
              let finalStartDate;

              if (this.timeline.options.showHolidays) {
                const sourceEndDateObj = new Date(sourceEndDate);
                sourceEndDateObj.setDate(sourceEndDateObj.getDate() + 1);
                const newStartDate = this.timeline.formatDate(sourceEndDateObj, 'y-m-d', 0);

                finalStartDate = this.timeline.dataManager.calculateWorkingEndDate(newStartDate, numGap - 1);
              } else {
                const newStartDateObj = new Date(sourceEndDate);
                newStartDateObj.setDate(newStartDateObj.getDate() + numGap);
                finalStartDate = this.timeline.formatDate(newStartDateObj, 'y-m-d', 0);
              }

              toBlock.startDate = finalStartDate;

              this.timeline.dataManager.updateBlockDates(toBlock);
              this.timeline.domRenderer.renderBlocks();
              this.updateConnections();

              await this.timeline.sendBlockUpdateToAPI({
                id: toBlock.id,
                tempId: toBlock.id || toBlock.tempId,
                row_index: toBlock.rowIndex,
                start_date: toBlock.startDate,
                duration: toBlock.duration,
                color: toBlock.color,
                display_text: toBlock.displayText,
                icon: toBlock.icon,
                progress: toBlock.progress
              });
            }
          }
        }
      } else {
        console.error('Failed to update connection gap on server:', connectionId);

        connection.gap = originalGap;

        this.timeline.showNotification(
          this.timeline.t('Failed to update connection gap on server: {message}', {message: result?.error || 'Unknown error'}),
          'error'
        );
      }
    }
  }

  /**
   * Synchronize connected blocks when a block moves
   * @param {Object} movedBlock - Block that was moved
   * @param {number} dayChange - Day change amount
   */
  syncConnectedBlocks(movedBlock, dayChange) {
    if (!this.timeline.options.syncConnectedBlocks || dayChange === 0) {
      return;
    }

    const updatedBlocks = new Map();
    updatedBlocks.set(movedBlock.id, true);

    this.syncDependentBlocks(movedBlock, updatedBlocks, 0, dayChange);
  }

  /**
   * Recursively synchronize dependent blocks
   * @param {Object} sourceBlock - Source block
   * @param {Map} updatedBlocks - Map of updated blocks
   * @param {number} depth - Recursion depth
   * @param {number} dayChange - Day change amount when initially moved block
   */
  syncDependentBlocks(sourceBlock, updatedBlocks, depth = 0, dayChange = 0) {
    if (depth > 100) {
      console.warn('Excessive recursion detected, possible cycle in connections');
      return;
    }

    const connections = this.getConnectionsFromBlock(sourceBlock.id);

    for (const connection of connections) {
      const dependentId = connection.toId;

      if (updatedBlocks.has(dependentId)) continue;

      const block = this.timeline.dataManager.blocks.find(b => b.id === dependentId);
      if (!block) continue;

      const gap = connection.gap !== undefined ? connection.gap : 1;

      const newStartIndex = block.startDay + dayChange;

      const finalStartDate = this.timeline.workingDaysManager.indexToDate(newStartIndex);

      if (finalStartDate) {
        block.startDate = finalStartDate;

        block.startDay = newStartIndex;
        block.left = newStartIndex * this.timeline.options.itemWidth;

        this.timeline.dataManager.updateBlockDates(block);

        const blockElement = document.querySelector(`.task-block[data-id="${block.id}"]`);
        if (blockElement) {
          this.timeline.domRenderer.renderBlock(block, blockElement);
        }

        updatedBlocks.set(block.id, true);

        this.timeline.dataManager.markBlockAsChanged(block);

        this.syncDependentBlocks(block, updatedBlocks, depth + 1, dayChange);
      }

    }
  }

  /**
   * Get all connections
   * @returns {Array} - Array of all connections
   */
  getConnections() {
    if (this.dependencyManager && this.dependencyManager.connections) {
      return this.dependencyManager.connections;
    } else if (this.connections) {
      return this.connections;
    }
    return [];
  }

  /**
   * Get a connection by ID or tempId
   * @param {string|number} id - Connection ID or tempId
   * @returns {Object|undefined} - Connection object
   */
  getConnection(id) {
    return this.dependencyManager.connections.find(
      conn => conn.id == id || conn.tempId == id
    );
  }

  /**
   * Get connections from a block
   * @param {string|number} blockId - Block ID
   * @returns {Array} - Array of connections
   */
  getConnectionsFromBlock(blockId) {
    return this.dependencyManager.connections.filter(conn => conn.fromId == blockId);
  }

  /**
   * Get connections to a block
   * @param {string|number} blockId - Block ID
   * @returns {Array} - Array of connections
   */
  getConnectionsToBlock(blockId) {
    return this.dependencyManager.connections.filter(conn => conn.toId == blockId);
  }

  /**
   * ส่งข้อมูลการเชื่อมโยงไปยัง API โดยตรง
   * @param {Object} connection - ข้อมูลการเชื่อมโยงที่ต้องการส่ง
   * @returns {Promise<Object>} - ผลลัพธ์จาก API
   */
  async sendConnectionToAPI(connection) {
    const connectionId = connection.id || connection.tempId;

    if (!this.timeline._canUpdateConnection(connectionId)) {
      return {success: false, error: 'Too frequent updates'};
    }

    this.timeline._recordConnectionUpdate(connectionId, true);

    try {
      if (!this.timeline.currentProject) {
        this.timeline._recordConnectionUpdate(connectionId, false);
        return {success: false, message: 'No active project'};
      }

      const connectionData = {
        id: connection.id,
        tempId: connection.tempId,
        fromId: connection.fromId,
        toId: connection.toId,
        gap: connection.gap || 1,
        type: connection.type || 'default'
      };

      if (window.apiHandler) {
        const result = await window.apiHandler.saveConnection(connectionData);

        this.timeline._recordConnectionUpdate(connectionId, false);

        if (result && result.success) {
          if (result.data && result.data.data && result.data.data.connectionId) {
            connection.id = result.data.data.connectionId;

            this.updateConnections();
          }

          return result;
        } else {
          console.error('Failed to update connection on server:', connection.tempId);
          return {success: false, error: result?.error || 'Unknown error'};
        }
      } else {
        this.timeline._recordConnectionUpdate(connectionId, false);

        return {success: false, error: 'No API handler available'};
      }
    } catch (error) {
      console.error('Error sending connection to API:', error);

      this.timeline._recordConnectionUpdate(connectionId, false);

      return {success: false, error: error.message};
    }
  }

  /**
   * Centralized method to delete a connection with complete error handling
   * This is the main entry point for deleting connections
   * @param {string|number} connectionId - Connection ID to delete
   * @returns {Promise<Object>} - Result object with success status
   */
  async deleteConnection(connectionId) {
    if (!connectionId) {
      const error = 'Connection ID is required';
      console.error(error);
      this.timeline.showNotification(this.timeline.t('Connection ID is required'), 'error');
      return {success: false, error};
    }

    try {
      const connection = this.getConnection(connectionId);

      if (!connection) {
        const error = `Connection not found: ${connectionId}`;
        console.warn(error);
        this.timeline.showNotification(this.timeline.t('Connection not found'), 'warning');
        return {success: false, error};
      }

      const connectionDetails = {
        id: connection.id,
        tempId: connection.tempId,
        fromId: connection.fromId,
        toId: connection.toId,
        type: connection.type
      };

      this.dependencyManager.removeConnection(connectionId);

      this.updateConnections();

      let apiResult = {success: true};
      if (connection.id) {
        apiResult = await this.deleteConnectionAPI(connection.id);
      }

      if (apiResult && apiResult.success) {
        this.timeline.showNotification(this.timeline.t('Connection deleted successfully'), 'success');

        this.timeline.triggerDataChangeCallback();

        return {success: true, connection: connectionDetails};
      } else {
        console.error('Failed to delete connection from server, restoring:', apiResult);

        this.dependencyManager.addConnection(connection);
        this.updateConnections();

        const errorMessage = apiResult?.error || 'Unknown server error';
        this.timeline.showNotification(
          this.timeline.t('Failed to delete connection: {message}', {message: errorMessage}),
          'error'
        );

        return {success: false, error: errorMessage, restored: true};
      }
    } catch (error) {
      console.error('Error in deleteConnection:', error);

      this.timeline.showNotification(
        this.timeline.t('Error deleting connection: {message}', {message: error.message}),
        'error'
      );

      return {success: false, error: error.message};
    }
  }

  /**
   * ลบการเชื่อมโยงโดยส่งคำสั่งไปยัง API โดยตรง
   * @param {string|number} connectionId - รหัสการเชื่อมโยงที่ต้องการลบ
   * @returns {Promise<Object>} - ผลลัพธ์จาก API
   */
  async deleteConnectionAPI(connectionId) {
    if (!this.timeline.currentProject) return {success: false, message: 'No active project'};

    try {
      const data = {
        projectId: this.timeline.currentProject.id,
        connectionId: connectionId
      };

      return await window.apiHandler.sendRequest('delete_connection', 'POST', data);
    } catch (error) {
      console.error('Error deleting connection from API:', error);
      return {success: false, error: error.message};
    }
  }

  /**
   * Update connection gaps after server calculation
   * @param {Array} updatedConnections - Updated connections from server
   */
  updateConnectionGapsFromServer(updatedConnections) {
    if (!updatedConnections || !Array.isArray(updatedConnections)) {
      return;
    }

    updatedConnections.forEach(serverConn => {
      const localConn = this.getConnection(serverConn.id);

      if (localConn) {
        localConn.gap = serverConn.gap;

        if (serverConn.fromId !== undefined) localConn.fromId = serverConn.fromId;
        if (serverConn.toId !== undefined) localConn.toId = serverConn.toId;
      }
    });

    this.updateConnections();
  }
}

window.ConnectionManager = ConnectionManager;
